import type { ClientFlowAction } from '~/graphql/types.client';
import hasSingleConnectedBranch from '../hasSingleConnectedBranch';

/**
 * Given the currently selected (copied) actions, returns a Set of action IDs
 * that can be removed from the selection. An action can only be removed if it
 * is at the "boundary" of the selection - either at the top (IfElse actions are allowed
 * at the top only if they have fewer than 2 children in the selection) or at the bottom.
 *
 * In addition, removing an action must keep the remaining selection as a single
 * connected branch. This prevents ending up with disconnected actions that
 * cannot be pasted.
 */
const getRemovableFromCopyIds = ({
  selectedActions,
}: {
  selectedActions: Array<ClientFlowAction>;
}): Set<string> => {
  const selectedIds = new Set(selectedActions.map(a => a.id));
  const removableIds = new Set<string>();

  for (const action of selectedActions) {
    const hasParentInSelection =
      'parentIds' in action &&
      action.parentIds.length > 0 &&
      selectedIds.has(action.parentIds[0]);

    const hasChildInSelection = selectedActions.some(
      candidate =>
        'parentIds' in candidate && candidate.parentIds[0] === action.id,
    );

    const ifElseChildCountInSelection =
      action.__typename === 'FlowV2_Action_IfElse'
        ? [action.trueChildId, action.falseChildId].filter(
            id => id && selectedIds.has(id),
          ).length
        : 0;

    const hasIfElseChildInSelection = ifElseChildCountInSelection > 0;

    const isAtTop =
      !hasParentInSelection &&
      (action.__typename !== 'FlowV2_Action_IfElse' ||
        ifElseChildCountInSelection < 2);
    const isAtBottom = !hasChildInSelection && !hasIfElseChildInSelection;

    if (isAtTop || isAtBottom) {
      const remainingSelection = selectedActions.filter(
        a => a.id !== action.id,
      );

      if (remainingSelection.length === 0) {
        removableIds.add(action.id);
        continue;
      }

      if (!hasSingleConnectedBranch(remainingSelection)) continue;

      removableIds.add(action.id);
    }
  }

  return removableIds;
};

export default getRemovableFromCopyIds;
